import { Icon } from "@iconify/react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import type { CategoryDto } from "@/api/categories";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useI18n } from "@/locales/i18n-provider";
import { cn } from "@/utils";
import { buildTree, filterWithAncestors } from "@/utils/tree";

type CategoryTreeSelectProps = {
  categories: CategoryDto[];
  disabled?: boolean;
  noneLabel?: string;
  triggerClassName?: string;
  triggerIcon?: ReactNode;
  value: string | null;
  valueLabel?: string | null;
  onValueChange: (categoryId: string | null) => void;
};

/**
 * 分类树选择器：父级分类只负责展开层级，只有叶子分类可以被选中。
 * 搜索时保留命中项的祖先，让结果仍然带有完整的层级上下文。
 */
export function CategoryTreeSelect({
  categories,
  disabled = false,
  noneLabel,
  triggerClassName,
  triggerIcon,
  value,
  valueLabel,
  onValueChange,
}: CategoryTreeSelectProps) {
  const { intlLocale, t } = useI18n();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const categoriesById = useMemo(() => new Map(categories.map((category) => [category.id, category])), [categories]);
  const childCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const category of categories) {
      if (category.parentId && categoriesById.has(category.parentId)) {
        counts.set(category.parentId, (counts.get(category.parentId) ?? 0) + 1);
      }
    }
    return counts;
  }, [categories, categoriesById]);

  useEffect(() => {
    if (!value) return;
    setExpandedIds((previous) => {
      const next = new Set(previous);
      let current = categoriesById.get(value);
      while (current?.parentId) {
        next.add(current.parentId);
        current = categoriesById.get(current.parentId);
      }
      return next;
    });
  }, [categoriesById, value]);

  const resolvedNoneLabel = noneLabel ?? t("categorySelect.none");
  const normalizedSearch = search.trim().toLocaleLowerCase(intlLocale);
  const filteredCategories = useMemo(() => {
    if (!normalizedSearch) return categories;
    return filterWithAncestors(categories, (category) => `${category.name} ${category.path}`.toLocaleLowerCase(intlLocale).includes(normalizedSearch));
  }, [categories, intlLocale, normalizedSearch]);
  const rows = useMemo(() => buildTree(filteredCategories, (left, right) => left.name.localeCompare(right.name, intlLocale)), [filteredCategories, intlLocale]);
  const visibleIds = useMemo(() => new Set(filteredCategories.map((category) => category.id)), [filteredCategories]);

  const isRowVisible = (category: CategoryDto) => {
    if (normalizedSearch) return true;
    let parent = category.parentId ? categoriesById.get(category.parentId) : undefined;
    while (parent && visibleIds.has(parent.id)) {
      if (!expandedIds.has(parent.id)) return false;
      parent = parent.parentId ? categoriesById.get(parent.parentId) : undefined;
    }
    return true;
  };

  const choose = (categoryId: string | null) => {
    onValueChange(categoryId);
    setOpen(false);
    setSearch("");
  };

  const toggleExpanded = (categoryId: string) => {
    setExpandedIds((previous) => {
      const next = new Set(previous);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });
  };

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          aria-expanded={open}
          aria-haspopup="tree"
          aria-label={t("categorySelect.label")}
          className={cn("w-full justify-between px-3 text-left font-normal", triggerClassName)}
          disabled={disabled}
          role="combobox"
          type="button"
          variant="outline"
        >
          <span className="min-w-0 flex-1 truncate">{valueLabel ?? (value ? categoriesById.get(value)?.path : null) ?? resolvedNoneLabel}</span>
          {triggerIcon ?? <Icon aria-hidden="true" className="size-4 text-muted-foreground" icon="tabler:chevron-down" />}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="start" className="w-(--radix-popover-trigger-width) gap-1.5 p-1.5">
        <div className="relative">
          <Icon
            aria-hidden="true"
            className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            icon="tabler:search"
          />
          <Input
            aria-label={t("categorySelect.searchLabel")}
            autoFocus
            className="h-8 border-transparent bg-muted/60 pl-8 shadow-none"
            placeholder={t("categorySelect.searchPlaceholder")}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <div className="max-h-72 overflow-y-auto overscroll-contain">
          <div aria-label={t("categorySelect.treeLabel")} className="space-y-0.5 py-0.5 pr-1" role="tree">
            {!normalizedSearch ? (
              <button
                aria-selected={value === null}
                className={cn(
                  "flex h-8 w-full items-center rounded-md text-left text-sm transition-colors hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
                  value === null && "bg-muted font-medium",
                )}
                role="treeitem"
                type="button"
                onClick={() => choose(null)}
              >
                <span className="grid size-7 shrink-0 place-items-center">
                  <Icon aria-hidden="true" className="size-3.5 text-muted-foreground" icon="majesticons:tag-off-line" />
                </span>
                <span className="min-w-0 flex-1 truncate text-muted-foreground">{resolvedNoneLabel}</span>
                {value === null ? <Icon aria-hidden="true" className="size-4 text-primary" icon="tabler:check" /> : null}
              </button>
            ) : null}

            {rows.map(({ item: category, depth }) => {
              if (!isRowVisible(category)) return null;
              const childCount = childCounts.get(category.id) ?? 0;
              const expanded = normalizedSearch ? true : expandedIds.has(category.id);
              const selected = childCount === 0 && category.id === value;

              return (
                <div
                  aria-expanded={childCount > 0 ? expanded : undefined}
                  aria-level={depth + 1}
                  aria-selected={selected}
                  className={cn("group flex h-8 min-w-0 items-center rounded-md pr-2 transition-colors hover:bg-muted", selected && "bg-muted font-medium")}
                  key={category.id}
                  role="treeitem"
                  style={{ paddingInlineStart: Math.min(depth, 8) * 16 + 2 }}
                  tabIndex={-1}
                >
                  {childCount > 0 ? (
                    <button
                      aria-expanded={expanded}
                      aria-label={t(expanded ? "categorySelect.collapse" : "categorySelect.expand", { name: category.name })}
                      className="grid size-7 shrink-0 place-items-center rounded text-muted-foreground hover:bg-background/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                      disabled={Boolean(normalizedSearch)}
                      tabIndex={normalizedSearch ? -1 : 0}
                      type="button"
                      onClick={() => toggleExpanded(category.id)}
                    >
                      <Icon aria-hidden="true" className={cn("size-3.5 transition-transform", expanded && "rotate-90")} icon="tabler:chevron-right" />
                    </button>
                  ) : (
                    <span className="grid size-7 shrink-0 place-items-center text-muted-foreground/70">
                      <Icon aria-hidden="true" className="size-3.5" icon="tabler:tag" />
                    </span>
                  )}

                  {childCount > 0 ? (
                    <button
                      aria-expanded={expanded}
                      aria-label={t(expanded ? "categorySelect.collapse" : "categorySelect.expand", { name: category.name })}
                      className="min-w-0 flex-1 truncate py-1.5 text-left text-sm font-medium focus:outline-none focus-visible:underline focus-visible:underline-offset-4"
                      disabled={Boolean(normalizedSearch)}
                      title={category.path}
                      type="button"
                      onClick={() => toggleExpanded(category.id)}
                    >
                      {category.name}
                    </button>
                  ) : (
                    <button
                      className="min-w-0 flex-1 truncate py-1.5 text-left text-sm focus:outline-none focus-visible:underline focus-visible:underline-offset-4"
                      title={category.path}
                      type="button"
                      onClick={() => choose(category.id)}
                    >
                      {category.name}
                    </button>
                  )}
                  {selected ? <Icon aria-hidden="true" className="ml-2 size-4 shrink-0 text-primary" icon="tabler:check" /> : null}
                </div>
              );
            })}

            {normalizedSearch && rows.length === 0 ? (
              <p className="px-3 py-8 text-center text-sm text-muted-foreground">{t("categorySelect.noMatches")}</p>
            ) : null}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
